/**
 * Shared card line parser
 *
 * Single source of truth for parsing text card lines like:
 * - "4 Lightning Bolt (M10) 146"
 * - "4x Lightning Bolt (M10)"
 * - "4 Lightning Bolt"
 * - "Lightning Bolt"
 * - With trailing foil/etched markers: "4 Lightning Bolt (M10) 146 *F*"
 *
 * Used by: MTGA parser, deck import, list import.
 */
import type { Finish } from '@/types/card';

// ============================================================================
// Types
// ============================================================================

export interface ParsedCardLine {
  name: string;
  quantity: number;
  setCode: string | null;
  collectorNumber: string | null;
  finish: Finish;
}

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Strip trailing foil/etched markers (`*F*` or `*E*`) from a line.
 * Returns the cleaned line and the parsed finish type.
 */
export function stripFinishMarker(line: string): { cleaned: string; finish?: Finish } {
  const match = line.match(/^(.+?)\s+\*([FE])\*\s*$/);
  if (match) {
    return { cleaned: match[1].trim(), finish: match[2] === 'F' ? 'foil' : 'etched' };
  }
  return { cleaned: line };
}

/**
 * Normalize split card names from Moxfield's " / " to Scryfall's " // " convention.
 */
export function normalizeSplitCardName(name: string): string {
  return name.replace(/\s+\/\s+/g, ' // ');
}

/**
 * Parse a single text line into a card entry.
 *
 * Handles all common text formats:
 * - Full MTGA: "4 Lightning Bolt (M10) 146"
 * - Set only: "4 Lightning Bolt (M10)"
 * - Simple: "4 Lightning Bolt" or "4x Lightning Bolt"
 * - Name only: "Lightning Bolt" (quantity defaults to 1)
 *
 * Automatically strips `*F*`/`*E*` foil markers and normalizes split card names.
 *
 * @returns Parsed card data, or null if the line cannot be parsed.
 */
export function parseCardLine(line: string): ParsedCardLine | null {
  const { cleaned, finish } = stripFinishMarker(line);

  if (!cleaned) return null;

  const resolvedFinish = finish ?? 'nonfoil';

  // Try full MTGA: "4 Lightning Bolt (M10) 146" or "4x Lightning Bolt (M10) 146"
  const fullMatch = cleaned.match(/^(\d+)x?\s+(.+?)\s+\(([A-Za-z0-9]+)\)\s+(\S+)$/);
  if (fullMatch) {
    return {
      name: normalizeSplitCardName(fullMatch[2].trim()),
      quantity: parseInt(fullMatch[1], 10),
      setCode: fullMatch[3].toLowerCase(),
      collectorNumber: fullMatch[4],
      finish: resolvedFinish,
    };
  }

  // Try set only: "4 Lightning Bolt (M10)" or "4x Lightning Bolt (M10)"
  const setOnlyMatch = cleaned.match(/^(\d+)x?\s+(.+?)\s+\(([A-Za-z0-9]+)\)$/);
  if (setOnlyMatch) {
    return {
      name: normalizeSplitCardName(setOnlyMatch[2].trim()),
      quantity: parseInt(setOnlyMatch[1], 10),
      setCode: setOnlyMatch[3].toLowerCase(),
      collectorNumber: null,
      finish: resolvedFinish,
    };
  }

  // Try simple: "4 Lightning Bolt" or "4x Lightning Bolt"
  const simpleMatch = cleaned.match(/^(\d+)x?\s+(.+)$/);
  if (simpleMatch) {
    return {
      name: normalizeSplitCardName(simpleMatch[2].trim()),
      quantity: parseInt(simpleMatch[1], 10),
      setCode: null,
      collectorNumber: null,
      finish: resolvedFinish,
    };
  }

  // Name only: "Lightning Bolt" (not starting with a digit)
  if (!cleaned.match(/^\d/)) {
    return {
      name: normalizeSplitCardName(cleaned),
      quantity: 1,
      setCode: null,
      collectorNumber: null,
      finish: resolvedFinish,
    };
  }

  return null;
}
